/**
 * RPC Client for Ditto Big Peer HTTP/RPC API
 *
 * Module providing helper functions to interact with the Big Peer HTTP/RPC API.
 * Supports both OAuth and API key authentication.
 */

import axios from 'axios'

import {
  AddRoleRequestWire,
  CreateRoleInput,
  DQLHTTPResponse,
  ExecuteDQLParams,
  ExecuteRemoteQueryParams,
  GenerateWebhookSecretParams,
  GetRolesPageResponseWire,
  GetRolesResponseWire,
  GetUsersParams,
  isDQLHTTPResponse,
  isGetRolesResponse,
  isLegacyFindByIdResponse,
  isLegacyFindResponse,
  isRbacApiError,
  isRemoteQueryHTTPResponse,
  LegacyFindByIdParams,
  LegacyFindByIdResponse,
  LegacyFindParams,
  LegacyFindResponse,
  LegacyRemoveParams,
  LegacyUpdateParams,
  LegacyUpsertParams,
  RbacApiError,
  RemoteQueryHTTPResponse,
  Role,
  RoleCollectionPermission,
  RoleCollectionPermissionValue,
  RoleDocWire,
  RolePermissions,
  RolePermissionsWire,
  RolesPage,
  RotateWebhookSecretParams,
  RpcClientOptions,
  UpdateUserRolesRequestWire,
  UpdateUserRolesResponse,
  UploadAttachmentResponse,
  User,
  UsersPage,
  WebhookSecretParams,
  WebhookSecretResponse,
  WebhookSecretsListResponse,
} from '../types'

export type { WebhookSecretResponse }

// ============================================================================
// Constants
// ============================================================================

/** Hydra client ID */
export const CLIENT_ID = 'AAAAAAAAAAAAAAAAAAAAew=='

type RpcApiVersion = 'v1' | 'v2' | 'v3' | 'v4' | 'v5'

// ============================================================================
// URL Generators
// ============================================================================

/** Generates the URL to access the RPC API. Exported for testing. */
export const rpcApiUrl = (
  appDomain: string,
  appId: string,
  version: RpcApiVersion,
  store = true,
) => {
  const hasProtocol =
    appDomain.startsWith('http://') || appDomain.startsWith('https://')

  const appSegment = appDomain.includes(appId)
    ? appDomain
    : `${appDomain}/${appId}`

  const baseUrl = hasProtocol ? appSegment : `https://${appSegment}`
  return `${baseUrl}/api/${version}${store ? '/store' : ''}`
}

/** Generates the URL for the Find method of the RPC API */
export const rpcApiFindUrl = (appDomain: string, appId: string) =>
  rpcApiUrl(appDomain, appId, 'v4') + '/find'

/** Generates the URL for the FindById method of the RPC API */
export const rpcApiFindByIdUrl = (appDomain: string, appId: string) =>
  rpcApiUrl(appDomain, appId, 'v4') + '/findbyid'

/** Generates the URL for the Write method of the RPC API */
export const rpcApiWriteUrl = (appDomain: string, appId: string) =>
  rpcApiUrl(appDomain, appId, 'v4') + '/write'

/** Generates the URL for the Count method of the RPC API */
export const rpcApiCountUrl = (appDomain: string, appId: string) =>
  rpcApiUrl(appDomain, appId, 'v3') + '/count'

export const rpcApiExecuteDqlQueryUrl = (
  appDomain: string,
  appId: string,
  version?: RpcApiVersion,
) => rpcApiUrl(appDomain, appId, version ?? 'v4') + '/execute'

export const rpcApiExecuteRemoteQueryUrl = (appDomain: string, appId: string) =>
  rpcApiUrl(appDomain, appId, 'v5', false) + '/sync/remote_execute'

export const rpcApiAttachmentUrl = (appDomain: string, appId: string) =>
  rpcApiUrl(appDomain, appId, 'v4', false) + '/attachments'

export const rpcApiAuthWebhookSecretUrl = (appDomain: string, appId: string) =>
  rpcApiUrl(appDomain, appId, 'v4', false) + '/auth/webhook/secret'

export const rpcApiAuthRolesUrl = (appDomain: string, appId: string) =>
  rpcApiUrl(appDomain, appId, 'v4', false) + '/auth/roles'

export const rpcApiAuthUsersUrl = (appDomain: string, appId: string) =>
  rpcApiUrl(appDomain, appId, 'v4', false) + '/auth/users'

// ============================================================================
// Authentication
// ============================================================================

/**
 * Function used for retrieving a JWT access token from the RPC client, given a local token.
 * This will rely on the portal token validation webhook to authenticate the user.
 *
 * Used for OAuth authentication flow only.
 */
export const rpcLogin = async (
  appDomain: string,
  appId: string,
  provider: string,
  userToken: string,
  options: RpcClientOptions = {},
): Promise<{
  accessToken: string
  tokenExpiration: string
}> => {
  const { onNotification } = options

  // Set up gradual notifications for long-running requests
  const warningTimer1 = setTimeout(() => {
    onNotification?.('info', 'Connection is taking longer than expected...')
  }, 10000)

  const warningTimer2 = setTimeout(() => {
    onNotification?.('warning', 'Still waiting for response from the app...')
  }, 20000)

  try {
    const hasProtocol =
      appDomain.startsWith('http://') || appDomain.startsWith('https://')
    const baseUrl = hasProtocol ? appDomain : `https://${appDomain}`

    const response = await axios.post(
      `${baseUrl}/_ditto/auth/http_login`,
      `appId=${appId}&token=${userToken}&provider=${provider}`,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        timeout: 30000,
      },
    )

    return {
      accessToken: response.data.httpToken,
      tokenExpiration: response.data.tokenExpiry,
    }
  } catch (error) {
    const errorMessage = getErrorMessage(error)
    onNotification?.('error', errorMessage)
    throw error
  } finally {
    // Always clear timers, whether success or failure
    clearTimeout(warningTimer1)
    clearTimeout(warningTimer2)
  }
}

// Process rpcLogin error messages
function getErrorMessage(error: unknown): string {
  if (!axios.isAxiosError(error)) {
    return 'Unexpected error. Please try again.'
  }

  if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
    return 'Connection timed out. Please try again.'
  }

  if (error.response) {
    const message = error.response.data?.message || error.message
    return `Authentication failed: ${message}`
  }

  if (error.request) {
    return 'No response from app. Check your connection.'
  }

  return `Connection error: ${error.message}`
}

/**
 * Generate default headers for RPC requests
 * Supports both OAuth tokens and API keys
 */
const rpcDefaultHeaders = (accessToken: string) => ({
  'X-DITTO-CLIENT-ID': CLIENT_ID,
  'Content-Type': 'application/json',
  Authorization: `Bearer ${accessToken}`,
})

// ============================================================================
// Collection Operations
// ============================================================================

/** Function used to check whether a collection is empty using an app's RPC endpoint */
export const isEmptyCollection = async (
  collection: string,
  query: string,
  appId: string,
  appDomain: string,
  accessToken: string,
) => {
  const response = await axios.post(
    rpcApiFindUrl(appDomain, appId),
    {
      collection,
      query,
      limit: 1,
    },
    {
      headers: rpcDefaultHeaders(accessToken),
    },
  )

  return !(response.data?.documents || []).length
}

export const getCollectionDocumentCount = async (
  collection: string,
  query: string,
  appId: string,
  appDomain: string,
  accessToken: string,
): Promise<number> => {
  const respones = await axios.post(
    rpcApiCountUrl(appDomain, appId),
    {
      collection,
      query: query || 'true',
    },
    {
      headers: rpcDefaultHeaders(accessToken),
    },
  )

  return respones.data?.count || 0
}

// ============================================================================
// Legacy Find/Query Operations
// ============================================================================

export const executeLegacyFindQuery = async (
  { query, collection, args, options, sort, limit }: LegacyFindParams,
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<LegacyFindResponse> => {
  const { data, status } = await axios.post(
    rpcApiFindUrl(appDomain, appId),
    {
      query,
      collection,
      args,
      sort,
      limit,
      ...options,
    },
    {
      headers: rpcDefaultHeaders(accessToken),
    },
  )

  if (!isLegacyFindResponse(data)) {
    throw new Error('Invalid response received from RPC client', {
      cause: { status, data },
    })
  }

  return data
}

export const executeLegacyFindByIdQuery = async (
  { id, collection, options }: LegacyFindByIdParams,
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<LegacyFindByIdResponse | null> => {
  const { data, status } = await axios.post(
    rpcApiFindByIdUrl(appDomain, appId),
    {
      id,
      collection,
      ...options,
    },
    {
      headers: rpcDefaultHeaders(accessToken),
    },
  )

  if (!isLegacyFindByIdResponse(data)) {
    throw new Error('Invalid response received from RPC client', {
      cause: { status, data },
    })
  }

  if (!data.id || !data.fields) {
    return null
  }

  return data
}

// ============================================================================
// Legacy Write Operations
// ============================================================================

export const executeLegacyUpdate = async (
  { query, collection, updates }: LegacyUpdateParams,
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<unknown> => {
  const response = await axios.post(
    rpcApiWriteUrl(appDomain, appId),
    {
      commands: [
        {
          method: 'update',
          collection,
          query,
          commands: updates.map(({ method = 'set', ...rest }) => ({
            method,
            ...rest,
          })),
        },
      ],
    },
    {
      headers: rpcDefaultHeaders(accessToken),
    },
  )

  return response.data
}

export const executeLegacyUpsert = async (
  { collection, id, value, valueTypeOverrides }: LegacyUpsertParams,
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<unknown> => {
  const response = await axios.post(
    rpcApiWriteUrl(appDomain, appId),
    {
      commands: [
        {
          method: 'upsert',
          collection,
          id,
          value,
          valueTypeOverrides,
        },
      ],
    },
    {
      headers: rpcDefaultHeaders(accessToken),
    },
  )

  return response.data
}

export const executeLegacyRemove = async (
  { query, collection }: LegacyRemoveParams,
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<unknown> => {
  const response = await axios.post(
    rpcApiWriteUrl(appDomain, appId),
    {
      commands: [
        {
          method: 'remove',
          collection,
          query,
        },
      ],
    },
    {
      headers: rpcDefaultHeaders(accessToken),
    },
  )

  return response.data
}

// ============================================================================
// DQL Query Operations
// ============================================================================

export const executeDqlQuery = async (
  query: string,
  appDomain: string,
  appId: string,
  accessToken: string,
  { args, strictMode }: ExecuteDQLParams = {},
  options: RpcClientOptions = {},
): Promise<DQLHTTPResponse> => {
  const { onError } = options
  const startTime = performance.now()

  try {
    const response = await axios.post(
      rpcApiExecuteDqlQueryUrl(appDomain, appId, strictMode ? 'v4' : 'v5'),
      {
        statement: query,
        args,
      },
      {
        headers: rpcDefaultHeaders(accessToken),
        // We don't want to throw ANY errors here, as we are generally always expecting a valid response
        // object. We will need to validate that it matches the expected format.
        validateStatus: () => true,
      },
    )

    const executionTime = performance.now() - startTime

    // NOTE: We can also add logic to verify the Query-Subtype header is present to determine validity
    // of the response object. Since that doesn't validate the _actual_ structure of what we received, I
    // opted to go this route instead.
    const isValid = isDQLHTTPResponse(response.data)
    if (!isValid) {
      const fallback =
        response.status > 200
          ? `Request could not be completed: ${response.statusText}`
          : 'An invalid response was received. Please try again'
      const error = new Error(response.data?.error?.description || fallback)

      onError?.(error, {
        operation: 'executeDqlQuery',
        query,
        appDomain,
        appId,
        strictMode: strictMode ?? false,
        httpStatus: response.status,
        executionTimeMs: Math.round(executionTime),
        errorType: 'validation_failed',
      })

      throw error
    }

    const responseData = response.data as DQLHTTPResponse

    // Check for DQL errors in response payload
    if (responseData.error && 'description' in responseData.error) {
      const dqlError = new Error(responseData.error.description)

      onError?.(dqlError, {
        operation: 'executeDqlQuery',
        query,
        appDomain,
        appId,
        strictMode: strictMode ?? false,
        httpStatus: response.status,
        executionTimeMs: Math.round(executionTime),
        errorType: 'dql_error',
        queryType: responseData.queryType,
      })

      // Don't throw - this is reported but the response may still contain partial results
    }

    // Report slow queries (>5 seconds) as performance issues
    if (executionTime > 5000) {
      onError?.(new Error('Slow query detected'), {
        operation: 'executeDqlQuery',
        query,
        appDomain,
        appId,
        strictMode: strictMode ?? false,
        httpStatus: response.status,
        executionTimeMs: Math.round(executionTime),
        errorType: 'performance',
        queryType: responseData.queryType,
        level: 'warning',
      })
    }

    return responseData
  } catch (error) {
    const executionTime = performance.now() - startTime
    // Capture error context for tracking
    const errorContext = {
      operation: 'executeDqlQuery',
      query,
      appDomain,
      appId,
      strictMode: strictMode ?? false,
      executionTimeMs: Math.round(executionTime),
    }

    if (axios.isAxiosError(error) && error.response) {
      const { message } = (error.response.data as { message?: string }) || {}
      const errorToThrow = new Error(message || error.message)

      onError?.(errorToThrow, {
        ...errorContext,
        httpStatus: error.response.status,
        errorType:
          error.response.status >= 500 ? 'server_error' : 'client_error',
      })

      throw errorToThrow
    } else if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
      onError?.(error, {
        ...errorContext,
        errorType: 'timeout',
      })

      throw error
    } else if (axios.isAxiosError(error)) {
      onError?.(error, {
        ...errorContext,
        errorType: 'network_error',
      })

      throw error
    } else {
      onError?.(error, {
        ...errorContext,
        errorType: 'unknown',
      })

      throw error
    }
  }
}

// ============================================================================
// Remote Query Operations
// ============================================================================

export const executeRemoteQuery = async (
  query: string,
  appDomain: string,
  appId: string,
  accessToken: string,
  { args, devicePeerKey }: ExecuteRemoteQueryParams = {},
  options: RpcClientOptions = {},
): Promise<RemoteQueryHTTPResponse> => {
  const { onError } = options
  const startTime = performance.now()

  try {
    const response = await axios.post(
      rpcApiExecuteRemoteQueryUrl(appDomain, appId),
      {
        statement: query,
        args,
      },
      {
        headers: rpcDefaultHeaders(accessToken),
        validateStatus: () => true,
      },
    )

    const executionTime = performance.now() - startTime

    if (!response) {
      throw new Error('No response received')
    }

    // Note, validation here is a bit messy but validation was failing even when it
    // shouldn't. So, to ensure it's not an issue with axios and the `data` property
    // I've added a check for the `data` property being undefined or null as well.
    const isValid = isRemoteQueryHTTPResponse(response?.data || response)
    if (!isValid) {
      const fallback =
        response.status > 200
          ? `Request could not be completed: ${response.statusText}`
          : 'An invalid response was received. Please try again'
      const error = new Error(response.data?.error?.description || fallback)

      onError?.(error, {
        operation: 'executeRemoteQuery',
        query,
        appDomain,
        appId,
        devicePeerKey,
        httpStatus: response.status,
        executionTimeMs: Math.round(executionTime),
        errorType: 'validation_failed',
      })

      throw error
    }

    const responseData = (response?.data || response) as RemoteQueryHTTPResponse

    // Check for top-level DQL errors
    if (responseData.error && 'description' in responseData.error) {
      const dqlError = new Error(responseData.error.description)

      onError?.(dqlError, {
        operation: 'executeRemoteQuery',
        query,
        appDomain,
        appId,
        devicePeerKey,
        httpStatus: response.status,
        executionTimeMs: Math.round(executionTime),
        errorType: 'dql_error',
        errorSource: 'top_level',
      })
    }

    // Check for peer-specific DQL errors
    responseData.result?.forEach((peerResult, index) => {
      if (peerResult.error && 'description' in peerResult.error) {
        const peerError = new Error(peerResult.error.description)

        onError?.(peerError, {
          operation: 'executeRemoteQuery',
          query,
          appDomain,
          appId,
          devicePeerKey,
          httpStatus: response.status,
          executionTimeMs: Math.round(executionTime),
          peerExecutionTimeMs: peerResult.elapsedMilliseconds,
          errorType: 'dql_error',
          errorSource: 'peer',
          peerIndex: index,
          peerMetadata: peerResult.peer,
        })
      }
    })

    // Report slow queries (>5 seconds) as performance issues
    if (executionTime > 5000) {
      onError?.(new Error('Slow query detected'), {
        operation: 'executeRemoteQuery',
        query,
        appDomain,
        appId,
        devicePeerKey,
        httpStatus: response.status,
        executionTimeMs: Math.round(executionTime),
        errorType: 'performance',
        level: 'warning',
      })
    }

    return responseData
  } catch (error) {
    const executionTime = performance.now() - startTime
    const errorContext = {
      operation: 'executeRemoteQuery',
      query,
      appDomain,
      appId,
      devicePeerKey,
      executionTimeMs: Math.round(executionTime),
    }

    if (axios.isAxiosError(error) && error.response) {
      const { message } = (error.response.data as { message?: string }) || {}
      const errorToThrow = new Error(message || error.message)

      onError?.(errorToThrow, {
        ...errorContext,
        httpStatus: error.response.status,
        errorType:
          error.response.status >= 500 ? 'server_error' : 'client_error',
      })

      throw errorToThrow
    } else if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
      onError?.(error, {
        ...errorContext,
        errorType: 'timeout',
      })

      throw error
    } else if (axios.isAxiosError(error)) {
      onError?.(error, {
        ...errorContext,
        errorType: 'network_error',
      })

      throw error
    } else {
      onError?.(error, {
        ...errorContext,
        errorType: 'unknown',
      })

      throw error
    }
  }
}

// ============================================================================
// Attachment Operations
// ============================================================================

export const downloadAttachmentUrl = (
  appDomain: string,
  appId: string,
): string => rpcApiAttachmentUrl(appDomain, appId)

export const downloadAttachment = async (
  id: string,
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<Uint8Array> => {
  const { data } = await axios.get(
    rpcApiAttachmentUrl(appDomain, appId) + `/${id}`,
    {
      headers: rpcDefaultHeaders(accessToken),
      responseType: 'arraybuffer',
    },
  )

  return new Uint8Array(Buffer.from(data, 'binary'))
}

export const uploadAttachment = async (
  file: File,
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<UploadAttachmentResponse> => {
  const formData = new FormData()
  formData.append('file', file)

  const { data } = await axios.post(
    rpcApiAttachmentUrl(appDomain, appId) + '/upload',
    formData,
    {
      headers: {
        ...rpcDefaultHeaders(accessToken),
        'Content-Type': 'multipart/form-data',
      },
    },
  )

  if (!data || !data.id) {
    throw new Error('Invalid response received from RPC client')
  }

  return data
}

// ============================================================================
// Auth Webhook Secret Operations
// ============================================================================

/** Generate a new webhook secret for HMAC-SHA256 signature verification. */
export const generateWebhookSecret = async (
  { providerName, notAfter }: GenerateWebhookSecretParams,
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<WebhookSecretResponse> => {
  const notBefore = new Date().toISOString()

  const { data } = await axios.post(
    rpcApiAuthWebhookSecretUrl(appDomain, appId),
    {
      provider: providerName,
      notBefore,
      notAfter,
    },
    {
      headers: rpcDefaultHeaders(accessToken),
    },
  )

  if (!data || !data.secret || !data.notBefore || !data.notAfter) {
    throw new Error('Invalid response received from webhook secret API')
  }

  return data
}

/** Get the current webhook secret for a provider. Returns null if none exists. */
export const getWebhookSecret = async (
  { providerName }: WebhookSecretParams,
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<WebhookSecretResponse | null> => {
  try {
    const { data } = await axios.get(
      rpcApiAuthWebhookSecretUrl(appDomain, appId),
      {
        headers: rpcDefaultHeaders(accessToken),
        params: { provider: providerName },
      },
    )

    if (!data || !data.secret) {
      return null
    }

    return data
  } catch (error) {
    // 404 means no secret exists
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

/** Delete the webhook secret for a provider. Requires the full secret object. */
export const deleteWebhookSecret = async (
  {
    providerName,
    secret,
    notBefore,
    notAfter,
  }: WebhookSecretParams & {
    secret: string
    notBefore: string
    notAfter: string
  },
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<void> => {
  await axios.delete(rpcApiAuthWebhookSecretUrl(appDomain, appId), {
    headers: rpcDefaultHeaders(accessToken),
    data: { provider: providerName, secret, notBefore, notAfter },
  })
}

/** Get all webhook secrets for a provider. Returns empty array if none exist. */
export const getWebhookSecrets = async (
  { providerName }: WebhookSecretParams,
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<WebhookSecretsListResponse> => {
  try {
    const { data } = await axios.get(
      rpcApiAuthWebhookSecretUrl(appDomain, appId),
      {
        headers: rpcDefaultHeaders(accessToken),
        params: { provider: providerName },
      },
    )

    if (!data) {
      return []
    }

    // Backend returns {} for no secrets, or {secret: [...]} for secrets
    if (typeof data === 'object' && Object.keys(data).length === 0) {
      return []
    }

    // Backend wraps array in {secret: [...]}
    if (data.secret && Array.isArray(data.secret)) {
      return data.secret
    }

    // Fallback: bare array
    if (Array.isArray(data)) {
      return data
    }

    // Single secret object (has secret string, not array)
    if (data.secret && typeof data.secret === 'string') {
      return [data]
    }

    return []
  } catch (error) {
    // 404 means no secrets exist
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return []
    }
    throw error
  }
}

/** Rotate a webhook secret: marks old secret as rotated and generates a new one. */
export const rotateWebhookSecret = async (
  { providerName, rotate, new: newValidity }: RotateWebhookSecretParams,
  appDomain: string,
  appId: string,
  accessToken: string,
): Promise<WebhookSecretResponse> => {
  const { data } = await axios.patch(
    rpcApiAuthWebhookSecretUrl(appDomain, appId),
    {
      provider: providerName,
      rotate,
      new: newValidity,
    },
    {
      headers: rpcDefaultHeaders(accessToken),
    },
  )

  if (!data || !data.secret || !data.notBefore || !data.notAfter) {
    throw new Error(
      'Invalid response received from webhook secret rotation API',
    )
  }

  return data
}

// ============================================================================
// RBAC Capability Probe
// ============================================================================

/**
 * Error raised for genuine RBAC probe failures (anything that is not one of
 * the recognized capability outcomes below).
 */
export class RbacClientError extends Error {
  public readonly statusCode?: number
  public readonly apiError?: RbacApiError
  /** What the response body said, absent when it carried no parseable body. */
  public readonly apiMessage?: string

  constructor(
    message: string,
    statusCode?: number,
    apiError?: RbacApiError,
    apiMessage?: string,
  ) {
    super(message)
    this.name = 'RbacClientError'
    this.statusCode = statusCode
    this.apiError = apiError
    this.apiMessage = apiMessage
  }
}

/** Matches the token exchange's own bound, the only other timeout in this file. */
const RBAC_REQUEST_TIMEOUT_MS = 30000

export interface RbacClientOptions {
  /** Optional custom auth headers (merged with the default Bearer token auth) */
  authHeaders?: Record<string, string>
  /** Error callback for handling errors */
  onError?: (error: RbacClientError) => void
  /** Notification callback for success/info/warning messages */
  onNotification?: (
    level: 'success' | 'info' | 'warning' | 'error',
    message: string,
  ) => void
  /** Enable debug logging */
  debug?: boolean
}

/** The result of probing GET /roles, discriminated on `outcome`. */
export type RolesCapabilityResult =
  | { outcome: 'available' }
  | { outcome: 'unauthenticated' }
  | { outcome: 'unsupported' }
  | { outcome: 'unauthorized' }
  | { outcome: 'unavailable' }
  | { outcome: 'error'; error: RbacClientError }

function buildRbacHeaders(
  accessToken: string,
  customHeaders?: Record<string, string>,
): Record<string, string> {
  const headers = rpcDefaultHeaders(accessToken)

  if (!customHeaders) {
    return headers
  }

  const merged: Record<string, string> = { ...headers }
  Object.entries(customHeaders).forEach(([key, value]) => {
    if (key === 'Authorization') return
    merged[key] = value
  })

  return merged
}

const hasWireMessage = (value: unknown): value is { message: string } =>
  typeof value === 'object' &&
  value !== null &&
  'message' in value &&
  typeof (value as { message: unknown }).message === 'string'

function toRbacClientError(
  error: unknown,
  options?: RbacClientOptions,
): RbacClientError {
  if (!axios.isAxiosError(error)) {
    const clientError = new RbacClientError(
      error instanceof Error
        ? error.message
        : 'Unexpected error while checking RBAC capability.',
    )
    options?.onError?.(clientError)
    return clientError
  }

  const statusCode = error.response?.status
  const responseData = error.response?.data

  let message = error.message || 'Unknown error occurred'
  let apiError: RbacApiError | undefined
  let apiMessage: string | undefined

  if (responseData && isRbacApiError(responseData)) {
    apiError = responseData
    // Falls back to the code because `message` is optional on this envelope,
    // and an absent apiMessage means no body arrived at all.
    apiMessage = apiError.message ?? apiError.code
    message = apiError.message || apiError.code || message
  } else if (hasWireMessage(responseData)) {
    // The Big Peer's error body carries no `code`.
    apiMessage = responseData.message
    message = responseData.message
  } else if (error.response?.statusText) {
    message = error.response.statusText
  }

  const clientError = new RbacClientError(
    message,
    statusCode,
    apiError,
    apiMessage,
  )

  if (options?.debug) {
    console.error('RBAC request error:', {
      message,
      statusCode,
      url: error.config?.url,
    })
  }

  options?.onError?.(clientError)
  return clientError
}

/**
 * Probes GET /roles to infer whether RBAC is usable for this app, without
 * throwing. Three of the six outcomes here (unsupported, unauthorized,
 * unavailable) cannot currently be produced by any deployed Big Peer: the
 * route is registered unconditionally and returns 200 whether RBAC is
 * configured or not, and neither 403 code is emitted by any shipped server
 * code today. They are mapped anyway so the caller starts behaving correctly
 * the moment a server emits them, and are exercised by tests only.
 */
export const probeRolesCapability = async (
  appDomain: string,
  appId: string,
  accessToken: string,
  options: RbacClientOptions = {},
): Promise<RolesCapabilityResult> => {
  try {
    await axios.get(rpcApiAuthRolesUrl(appDomain, appId), {
      headers: buildRbacHeaders(accessToken, options.authHeaders),
    })

    return { outcome: 'available' }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response

      if (status === 404) {
        return { outcome: 'unsupported' }
      }

      if (status === 401) {
        return { outcome: 'unauthenticated' }
      }

      if (status === 403 && isRbacApiError(data)) {
        if (data.code === 'ADMIN_REQUIRED') {
          return { outcome: 'unauthorized' }
        }

        if (data.code === 'ADMIN_GATE_UNAVAILABLE') {
          return { outcome: 'unavailable' }
        }
      }
    }

    return { outcome: 'error', error: toRbacClientError(error, options) }
  }
}

const isCursorPagedRoles = (
  body: GetRolesResponseWire,
): body is GetRolesPageResponseWire => Array.isArray(body.roles)

const byRoleName = (a: Role, b: Role) => a.name.localeCompare(b.name)

/**
 * One side of a collection permission, denied unless it arrives as a boolean or
 * an array of clause strings. An omitted side denies that side, it does not
 * leave it unspecified.
 */
const normalizePermissionSide = (
  value: unknown,
): RoleCollectionPermissionValue => {
  if (typeof value === 'boolean') {
    return value
  }

  if (
    Array.isArray(value) &&
    value.every((clause) => typeof clause === 'string')
  ) {
    return value
  }

  return false
}

/** A collection entry that is not an object carries no side to read, so it is fully denied. */
const normalizeCollectionPermission = (
  permission: unknown,
): RoleCollectionPermission => {
  if (typeof permission !== 'object' || permission === null) {
    return { read: false, write: false }
  }

  const { read, write } = permission as Record<string, unknown>
  return {
    read: normalizePermissionSide(read),
    write: normalizePermissionSide(write),
  }
}

const normalizeRolePermissions = (
  permissions: RolePermissionsWire | undefined,
): RolePermissions => {
  if (typeof permissions === 'string') {
    return permissions
  }

  // Catches undefined and null (typeof null === 'object') alongside anything
  // else that isn't a genuine collection-permissions map.
  if (typeof permissions !== 'object' || permissions === null) {
    return 'none'
  }

  return Object.fromEntries(
    Object.entries(permissions).map(([collection, permission]) => [
      collection,
      normalizeCollectionPermission(permission),
    ]),
  )
}

/** Codepoint order, which for the UUIDv7 version strings is creation order. */
const byVersionAscending = (a: RoleDocWire, b: RoleDocWire) => {
  if (a._id.version === b._id.version) {
    return 0
  }
  return a._id.version < b._id.version ? -1 : 1
}

/** The current version in a bucket, whatever order the bucket arrived in. */
const newestVersion = (versions: RoleDocWire[]): RoleDocWire => {
  const sorted = [...versions].sort(byVersionAscending)
  return sorted[sorted.length - 1]
}

const normalizeRoleDoc = (doc: RoleDocWire): Role => ({
  name: doc._id.name,
  version: doc._id.version,
  description: doc.description ?? '',
  collectionPermissions: normalizeRolePermissions(doc.collection_permissions),
  grantRemoteQuery: doc.grant_remote_query ?? false,
})

/**
 * Normalizes either GET /roles wire shape into a single page model, resolving
 * every field the backend may omit.
 */
export const normalizeRolesResponse = (body: unknown): RolesPage => {
  if (!isGetRolesResponse(body)) {
    return { roles: [], hasMore: false }
  }

  if (isCursorPagedRoles(body)) {
    return {
      roles: body.roles.map(normalizeRoleDoc).sort(byRoleName),
      hasMore: body.hasMore,
      cursor: body.cursor,
    }
  }

  const roles = Object.values(body.roles)
    .filter((versions) => versions.length > 0)
    .map((versions) => normalizeRoleDoc(newestVersion(versions)))
    .sort(byRoleName)

  return { roles, hasMore: false }
}

/**
 * Fetches the roles for an app. Throws an RbacClientError on failure.
 */
export const getRoles = async (
  appDomain: string,
  appId: string,
  accessToken: string,
  options: RbacClientOptions = {},
): Promise<RolesPage> => {
  let body: unknown

  try {
    const { data } = await axios.get(rpcApiAuthRolesUrl(appDomain, appId), {
      headers: buildRbacHeaders(accessToken, options.authHeaders),
    })
    body = data
  } catch (error) {
    throw toRbacClientError(error, options)
  }

  if (!isGetRolesResponse(body)) {
    throw new RbacClientError('Invalid response received from the roles API')
  }

  return normalizeRolesResponse(body)
}

/**
 * Creates or replaces a role. Throws an RbacClientError on failure. Returns
 * nothing, because the success response carries no usable fields on today's
 * deployments.
 */
export const createRole = async (
  appDomain: string,
  appId: string,
  accessToken: string,
  input: CreateRoleInput,
  options: RbacClientOptions = {},
): Promise<void> => {
  const body: AddRoleRequestWire = {
    name: input.name,
    doc: {
      roles_version: 'v1-preview',
      description: input.description,
      collection_permissions: input.collectionPermissions,
      grant_remote_query: input.grantRemoteQuery,
    },
  }

  try {
    await axios.post(rpcApiAuthRolesUrl(appDomain, appId), body, {
      headers: buildRbacHeaders(accessToken, options.authHeaders),
    })
  } catch (error) {
    throw toRbacClientError(error, options)
  }
}

/**
 * Deletes every version of a role by name. Throws an RbacClientError on
 * failure. Returns nothing, because the success response carries no usable
 * fields on today's deployments.
 */
export const deleteRole = async (
  appDomain: string,
  appId: string,
  accessToken: string,
  roleName: string,
  options: RbacClientOptions = {},
): Promise<void> => {
  const url = `${rpcApiAuthRolesUrl(appDomain, appId)}/${encodeURIComponent(roleName)}`

  try {
    await axios.delete(url, {
      headers: buildRbacHeaders(accessToken, options.authHeaders),
      // The confirmation dialog refuses to close while this is in flight, so a
      // request that never settles would leave the user shut inside it.
      timeout: RBAC_REQUEST_TIMEOUT_MS,
    })
  } catch (error) {
    throw toRbacClientError(error, options)
  }
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0

/** Strings only, which is all the server's own projection of a role list emits. */
const normalizeUserRoles = (roles: unknown): string[] =>
  Array.isArray(roles)
    ? roles.filter((role): role is string => typeof role === 'string')
    : []

/**
 * One user, with the wire defaults resolved. A missing id is fatal; everything
 * else clamps.
 */
const normalizeUserDoc = (doc: unknown): User => {
  const { userId, roles, identityVersion } = (doc ?? {}) as {
    userId?: unknown
    roles?: unknown
    identityVersion?: unknown
  }

  if (!isNonEmptyString(userId)) {
    throw new RbacClientError('Invalid user received from the users API')
  }

  const provisioned = isNonEmptyString(identityVersion)

  return {
    userId,
    roles: normalizeUserRoles(roles),
    identityVersion: provisioned ? identityVersion : undefined,
    isProvisioned: provisioned,
  }
}

/**
 * Normalizes the GET /auth/users wire shape into a page model. A malformed
 * envelope throws rather than resolving to an empty page.
 */
export const normalizeUsersResponse = (body: unknown): UsersPage => {
  const { users, hasMore, cursor } = (body ?? {}) as {
    users?: unknown
    hasMore?: unknown
    cursor?: unknown
  }

  if (!Array.isArray(users) || typeof hasMore !== 'boolean') {
    throw new RbacClientError('Invalid response received from the users API')
  }

  return {
    users: users.map(normalizeUserDoc),
    hasMore,
    cursor: isNonEmptyString(cursor) ? cursor : undefined,
  }
}

/**
 * Fetches a page of users for an app. Throws an RbacClientError on failure.
 */
export const getUsers = async (
  appDomain: string,
  appId: string,
  accessToken: string,
  params: GetUsersParams = {},
  options: RbacClientOptions = {},
): Promise<UsersPage> => {
  const query: Record<string, string | number> = {}

  // Only defined, non-empty values: the server reads `userId=` as a filter on
  // the empty id and answers with an empty page rather than an unfiltered one.
  if (params.userId) {
    query.userId = params.userId
  }

  if (params.cursor) {
    query.cursor = params.cursor
  }

  if (params.limit !== undefined) {
    query.limit = params.limit
  }

  let body: unknown

  try {
    const { data } = await axios.get(rpcApiAuthUsersUrl(appDomain, appId), {
      headers: buildRbacHeaders(accessToken, options.authHeaders),
      params: query,
    })
    body = data
  } catch (error) {
    throw toRbacClientError(error, options)
  }

  return normalizeUsersResponse(body)
}

/**
 * Replaces a user's entire role set: the roles passed here become the roles the
 * user holds, and an empty array clears them. Throws an RbacClientError on
 * failure.
 */
export const updateUserRoles = async (
  appDomain: string,
  appId: string,
  accessToken: string,
  userId: string,
  roles: string[],
  options: RbacClientOptions = {},
): Promise<UpdateUserRolesResponse> => {
  // A user id is an opaque IdP subject, so it can carry characters a path cannot.
  const url = `${rpcApiAuthUsersUrl(appDomain, appId)}/${encodeURIComponent(userId)}`
  const body: UpdateUserRolesRequestWire = { roles }

  let data: unknown

  try {
    const response = await axios.patch(url, body, {
      headers: buildRbacHeaders(accessToken, options.authHeaders),
      timeout: RBAC_REQUEST_TIMEOUT_MS,
    })
    data = response.data
  } catch (error) {
    throw toRbacClientError(error, options)
  }

  const { identityVersion, transactionId } = (data ?? {}) as {
    identityVersion?: unknown
    transactionId?: unknown
  }

  if (!isNonEmptyString(identityVersion) || typeof transactionId !== 'number') {
    throw new RbacClientError(
      'Invalid response received from the user roles API',
    )
  }

  return { identityVersion, transactionId }
}

/**
 * Removes a user from an app. Throws an RbacClientError on failure. Returns
 * nothing, because the success response carries no usable fields on today's
 * deployments.
 */
export const deleteUser = async (
  appDomain: string,
  appId: string,
  accessToken: string,
  userId: string,
  options: RbacClientOptions = {},
): Promise<void> => {
  const url = `${rpcApiAuthUsersUrl(appDomain, appId)}/${encodeURIComponent(userId)}`

  try {
    await axios.delete(url, {
      headers: buildRbacHeaders(accessToken, options.authHeaders),
      // Bounded because the confirmation refuses to dismiss while it is in flight.
      timeout: RBAC_REQUEST_TIMEOUT_MS,
    })
  } catch (error) {
    throw toRbacClientError(error, options)
  }
}
